import React, { useRef, useState } from "react"
import { graphql } from "gatsby"
import NewLayout from "../components/new_layout"
import SEO from "../components/seo"
import MainSection100 from "../components/Layout/main-section-100"
import Tag from "../components/v2023/UI/Tag/tag"
import ReactMarkdown from "react-markdown"
import rehypeRaw from "rehype-raw"
import LinkButton from "../components/v2023/UI/LinkButton/link-button"
import { Pricing } from "../components/v2025/pricing"
import { Comparative } from "../components/v2024/comparative"
import CarouselTouch from "../components/v2024/Carousel/carousel-touch"
import ModalLogin from "../components/v2024/Login/modal-login"
import MenuModalState from "../components/v2024/Login/modal-login-state"
import { ModalForm } from "../components/v2024/modal"

const FacturaElectronicaPricing = ({ data, pageContext }) => {
    const page = data.page
    const lang = pageContext.lang
    const seeMore = page.see_more
    const seeLess = page.see_less
    const [limitFree, setLimitFree] = useState(4)
    const [limitBasic, setLimitBasic] = useState(4)
    const [limitPro, setLimitPro] = useState(4)
    const scrollRef = useRef(null)
    const scrollRefFree = useRef(null)
    const scrollRefBasic = useRef(null)
    const scrollRefPro = useRef(null)
    const loginModalState = MenuModalState()
    const requestDemoModalState = MenuModalState()
    const contactUsModalState = MenuModalState()

    const scrollToSection = () => {
        scrollRef.current.scrollIntoView({
            behavior: "smooth"
        })
    }
    const scrollToFree = () => {
        scrollRefFree.current.scrollIntoView({
            behavior: "smooth"
        })
    }
    const scrollToBasic = () => {
        scrollRefBasic.current.scrollIntoView({
            behavior: "smooth"
        })
    }
    const scrollToPro = () => {
        scrollRefPro.current.scrollIntoView({
            behavior: "smooth"
        })
    }

    const normalizeComparativeData = (headers, comparative) => {
        const data = []
        const freeCharacteristics = []
        const basicCharacteristics = []
        const proCharacteristics = []

        comparative.forEach((item) => {
            freeCharacteristics.push({
                name: item.name,
                text: item.text_free,
                bool: item.free,
                tooltip: item.tooltip,
                seeButtons: {
                    seeMore,
                    seeLess
                },
                limit: limitFree,
                setLimit: setLimitFree
            })
            basicCharacteristics.push({
                name: item.name,
                text: item.text_basic,
                bool: item.basic,
                tooltip: item.tooltip,
                seeButtons: {
                    seeMore,
                    seeLess
                },
                limit: limitBasic,
                setLimit: setLimitBasic
            })
            proCharacteristics.push({
                name: item.name,
                text: item.text_pro,
                bool: item.pro,
                tooltip: item.tooltip,
                limit: limitPro,
                setLimit: setLimitPro
            })
        })

        headers.map((header, index) => {
            let characteristics = []
            let limit = null
            let setLimit = null
            let headerButton = null

            switch (index) {
                case 0:
                    characteristics = freeCharacteristics
                    limit = limitFree
                    setLimit = setLimitFree
                    headerButton = <button className="button button--sm" onClick={() => loginModalState.openMenu()}>
                        {header.button.text}
                    </button>
                    break
                case 1:
                    characteristics = basicCharacteristics
                    limit = limitBasic
                    setLimit = setLimitBasic
                    headerButton = <button className="button button--sm" onClick={() => requestDemoModalState.openMenu()}>
                        {header.button.text}
                    </button>
                    break
                case 2:
                    characteristics = proCharacteristics
                    limit = limitPro
                    setLimit = setLimitPro
                    headerButton = <button className="button button--sm" onClick={() => contactUsModalState.openMenu()}>
                        {header.button.text}
                    </button>
                    break
            }

            data.push({
                name: header.title,
                headerButton: headerButton,
                highlighted: header.highlight,
                characteristicsList: { characteristics: characteristics },
                seeButtons: {
                    seeMore,
                    seeLess
                },
                limit: limit,
                setLimit: setLimit,
                buttons: []
            })
        })

        return data
    }

    const comparativeData = normalizeComparativeData(page.table_headers, page.comparative_table)

    const states = [
        {
            'form': 'demo',
            'state': requestDemoModalState
        },
        {
            'form': 'contact',
            'state': contactUsModalState
        },
        {
            'form': 'login',
            'state': loginModalState
        }
    ]

    return (
        <NewLayout pageContext={pageContext}>
            <SEO
                lang={lang}
                title={page.seo.title}
                description={page.seo.meta_description}
                translates={pageContext.translates}
                image={page.seo.image_2.localFile.publicURL}
            />
            {/* MAIN SECTION */}
            <MainSection100 hero frameGray>
                <div data-aos="fade-up">
                    <Tag tag={page.tag} />
                </div>
                <div className="container__text" data-aos="fade-up">
                    <h1 className="hero">{page.title}</h1>
                    <ReactMarkdown children={page.description} rehypePlugins={[rehypeRaw]} />
                </div>
                <div data-aos="fade-up" className="hidden-md grid-lg-3">
                    {page.pricing_options.map((pricing_option, index) =>
                        <Pricing
                            index={index}
                            item={pricing_option}
                            formPricing={page.pricing_form}
                            states={states}
                            scrollToSection={scrollToSection}
                        />
                    )}
                </div>
            </MainSection100>
            <div data-aos="fade-up" className="show-md">
                <CarouselTouch
                    slides={page.pricing_options}
                    usePricingCarousel
                    modals={page.pricing_form}
                    states={states}
                    functions={[scrollToFree, scrollToBasic, scrollToPro]}
                    showIndicators
                    initialSlide={1}
                />
            </div>
            {/* CTA */}
            <MainSection100>
                <div className="tile tile--green">
                    <h3>{page.plan_cta.title}</h3>
                    <div className="tile__body">
                        <ReactMarkdown children={page.plan_cta.description} rehypePlugins={[rehypeRaw]} />
                    </div>
                    <LinkButton button={page.plan_cta.button} className="button" extras={<i className="icon-arrow-right"></i>} />
                </div>
            </MainSection100>
            {/* COMPARATIVE */}
            <MainSection100>
                <h2 ref={scrollRef}>{page.comparative_title}</h2>
                <Comparative
                    comparative={comparativeData}
                    thin={false}
                    headerTitle={page.comparative_header_text}
                    references={[scrollRefFree, scrollRefBasic, scrollRefPro]}
                />
            </MainSection100>
            {/* MODALS */}
            <ModalForm
                state={requestDemoModalState}
                divFormId={"form-pricing-basic"}
                form={page.request_demo_form}
            />
            <ModalForm
                state={contactUsModalState}
                divFormId={"form-pricing-pro"}
                form={page.contact_form}
            />
            <ModalLogin
                lang={lang}
                state={loginModalState}
                plan={page.sign_in_form}
                selectedPlan="factura_free"
            />
        </NewLayout>
    )
}

export default FacturaElectronicaPricing

export const facturaElectronicaPricingQuery = graphql`
    query ($langKey: String) {
      page: strapi2025FacturaElectronicaPricing(locale: { eq: $langKey }) {
        seo {
          title
          meta_description
          image_2 {
            localFile {
              publicURL
            }
          }
        }
        tag {
          name
          type
        }
        title
        description
        pricing_options {
          title
          price
          price_detail
          buttons {
            text
            url
            blank
          }
          description
          see_more {
            text
            url
            blank
          }
        }
        plan_cta {
          title
          description
          button {
            text
            url
            blank
          }
        }
        comparative_title
        comparative_header_text
        table_headers {
          title
          highlight
          button {
            text
            url
            blank
          }
        }
        comparative_table {
          name
          free
          basic
          pro
          text_free
          text_basic
          text_pro
          tooltip
        }
        request_demo_form {
          form_id
          portal_id
          region
          title
          description
        }
        contact_form {
          form_id
          portal_id
          region
          title
          description
        }
        pricing_form {
          title
          description
          pricing_option {
            quantity
            detail
            price
          }
        }
        sign_in_form {
			form_email
			or_sign_up
			login_text
			form_name
			form_tc_description
			form_repassword
			form_password
			title
			description
			button_google
			button_email
			form_lastname
			signup_text
			button_signup
			button_login
			remember_me
			forgot_password
			title_login
			description_login
			form_tc_info
		}
        see_more
        see_less
      }
    }
`